import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import {
  EntityConfiguration,
  EntityConfigurationEntityType,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import TextArea from "@/components/common/TextArea";

export default function ConfigurationEditor({
  conf,
  refreshConf,
}: {
  conf: EntityConfiguration;
  refreshConf: () => void;
}) {
  const [newConf, setNewConf] = useState(conf.value);
  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);
  const { t } = useTranslation();

  const { mutate: updateConf, isPending } = useUpdateConfiguration();

  useEffect(() => {
    setNewConf(conf.value);
  }, [conf.value]);

  const saveConf = useCallback(() => {
    setSaveResult(null);
    updateConf(
      {
        entityType: EntityConfigurationEntityType.BUILDING,
        buildingId: conf.entityId,
        configurationType: conf.type,
        configurationValue: newConf,
      },
      {
        onSuccess: () => {
          setSaveResult({
            success: true,
            result: null,
          });
          refreshConf();
          setTimeout(() => setSaveResult(null), 10000);
        },
        onError: (error) => {
          console.error("Failed to update configuration:", error);
          setSaveResult({
            success: false,
            result: undefined,
            message: error.message || t("settings.saveError"),
          });
          setTimeout(() => setSaveResult(null), 10000);
        },
      },
    );
  }, [conf, newConf, refreshConf, updateConf, t]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <TextArea value={newConf} onChange={(e) => setNewConf(e.target.value)} disabled={isPending} rows={6} />
        </div>

        <div className="flex items-center gap-4">
          <Button onClick={saveConf} disabled={isPending} loading={isPending}>
            {isPending ? t("settings.saving") : t("settings.save")}
          </Button>
          <div className="flex-grow">
            {saveResult && <ApiBanner apiResponse={saveResult} successMessage={t("settings.saveSuccess")} />}
          </div>
        </div>
      </div>
    </div>
  );
}
